import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { preventEnterSubmit } from '../../lib/form-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type {
  ClientProjectActivity,
  CreateClientProjectActivityInput,
  UpdateClientProjectActivityInput,
  ClientProject,
} from '@packages/types';
import { getProjectDisplayReference, MAX_ACTIVITY_NOTE_LENGTH } from '@packages/types';
import { useAuth } from '../../lib/auth/useAuth';
import { useAccount } from '../../lib/account/useAccount';
import { useCopy } from '../../hooks/useCopy';
import type { CopyFn } from '@packages/copy';
import {
  createClientProjectActivity,
  updateClientProjectActivity,
  setClientProjectActivityNote,
  approveClientProjectActivities,
} from '@packages/api/endpoints/client-project-activity.js';
import { listClientProjects, getClientProject } from '@packages/api/endpoints/client-project.js';
import { getStoredEmail } from '@packages/api/endpoints/email-inbox.js';
import { getStoredCalendarEvent } from '@packages/api/endpoints/calendar-connection.js';
import { useInvoicesForLineItems } from '../invoice/useInvoicesForLineItems';
import { MONEY_INPUT_REGEX } from '../../lib/money-input';
import { formatCount, toIsoDate } from '@packages/format';
import { toDateKey } from '../../lib/activity-grouping';
import { queryKeys, getNextPageParam, flattenPages, DEFAULT_LIST_PAGE_SIZE } from '@packages/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { Checkbox } from '../ui/Checkbox';
import { FormDialog } from '../ui/FormDialog';
import { Alert } from '../ui/Alert';
import { SaveErrorAlert } from '../SaveErrorAlert';
import { TextLink, TextButton } from '../ui/TextLink';
import { Reveal } from '../ui/Reveal';
import { useRevealGroup } from '../../hooks/useRevealGroup';
import { MatterPickerSkeleton } from '../matter/MatterPickerSkeleton';
import { useToast } from '../ui/Toast';
import { CalendarPopoverButton } from '../ui/CalendarPopoverButton';
import {
  ACTIVITY_TYPE_REGISTRY,
  getCaptureActivityTypes,
  UNIT_TYPES,
  UNIT_TYPE_REGISTRY,
  UnitTypeSchema,
  getTypeLabel,
  type ActivityType,
  type UnitType,
} from '@packages/types';
import { getPracticeFeatures } from '@packages/practice-config';
import {
  isHoursType,
  normaliseNote,
  parseRateInputToMinorUnits,
  ratePlaceholder,
  sameActivityDetails,
  UNASSIGNED_PERFORMER,
} from './ActivityWizardForm.helpers';
import { defaultFeeEarnerUserId, feeEarnerMembers as selectFeeEarnerMembers } from '../../lib/fee-earners';
import { describeRateHint, emailFixedRateConfigured, useActivityDefaultRate } from './useActivityDefaultRate';
import { buildIncrementSteering, totalMinutesFromInputs } from './increment-steering';
import { IncrementSteeringAlert } from './IncrementSteeringAlert';
import { ActivityAttachmentUploader } from './ActivityAttachmentUploader';
import { ActivityNoteField } from './ActivityNoteField';
import { uploadPendingAttachments } from './activity-attachment-upload';
import { EmailPreview } from './EmailPreview';
import { CalendarEventPreview } from './CalendarEventPreview';
import { hasSourceEmail as activityHasSourceEmail } from '../../lib/activity-source-email';
import { hasSourceCalendar as activityHasSourceCalendar } from '../../lib/activity-source-calendar';
import { activityEditBlockedReason, activityNoteEditable } from '../../lib/activity-permissions';
import { CalendarIcon } from '../ui/CalendarIcon';
import { invalidateActivityFinancialCaches } from '../../lib/financial-cache-invalidation';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

// Schema factory: the "Matter is required" message names the practice's
// dialect noun, so it must be built from a CopyFn resolved inside the
// component (see packages/copy/CLAUDE.md "Zod schema factories" for the
// pattern). The other messages here are dialect-neutral and stay literal.
function createWizardFormSchema(copy: CopyFn) {
  return z
    .object({
      type: z.string().nullable().optional(),
      clientId: z.string().min(1, 'Client is required'),
      clientProjectId: z.string().min(1, copy('activity:wizard.projectRequired')),
      performedByUserId: z.string().optional(),
      description: z.string().max(2000, 'Description must be 2000 characters or less').optional(),
      note: z
        .string()
        .max(MAX_ACTIVITY_NOTE_LENGTH, `Note must be ${formatCount(MAX_ACTIVITY_NOTE_LENGTH)} characters or less`)
        .optional(),
      date: z.string().min(1, 'Date is required'),
      unitType: UnitTypeSchema.optional(),
      isDisbursement: z.boolean().optional(),
      // For non-hours types: freeform decimal string
      units: z.union([z.literal(''), z.string().regex(MONEY_INPUT_REGEX, 'Enter a valid number')]),
      // For hours-based types: separate integer fields combined in toApiInput
      hoursInput: z.union([z.literal(''), z.string().regex(/^\d+$/, 'Enter whole hours')]).optional(),
      minutesInput: z.union([
        z.literal(''),
        z.string()
          .regex(/^\d+$/, 'Enter whole minutes')
          .refine((value) => parseInt(value, 10) <= 59, 'Enter 0-59 minutes'),
      ]).optional(),
      ratePerUnitMinorUnits: z.union([z.literal(''), z.string().regex(MONEY_INPUT_REGEX, 'Enter a valid amount')]),
    })
    .superRefine((data, ctx) => {
      // Description is always required on save. Step 1 (type selection) runs
      // with data.type === undefined - skip the check there so the user can
      // pick a type before the field is visible.
      const pastStep1 = data.type !== undefined;
      if (pastStep1 && (!data.description || data.description.trim() === '')) {
        ctx.addIssue({
          code: "custom",
          message: 'Description is required',
          path: ['description'],
        });
      }

      // unitType is required once the user has selected a type (past step 1)
      if (pastStep1 && !data.unitType) {
        ctx.addIssue({
          code: "custom",
          message: 'Unit type is required',
          path: ['unitType'],
        });
      }
    });
}

type WizardFormData = z.infer<ReturnType<typeof createWizardFormSchema>>;

// ---------------------------------------------------------------------------
// Form -> API mapping
// ---------------------------------------------------------------------------

// Fields shared by the create and update payloads: only keys both bodies accept,
// typed to satisfy both. The declared return type is what makes TypeScript reject
// a key only one of them takes (the update body is strict, so an extra key there
// is a 400): the builders below spread this object, and a spread is never checked
// for extra keys. performedByUserId is excluded because create and update map the
// UNASSIGNED_PERFORMER sentinel differently (see toCreateInput / toUpdateInput).
type SharedActivityInputKey = Exclude<
  keyof CreateClientProjectActivityInput & keyof UpdateClientProjectActivityInput,
  'performedByUserId'
>;
type SharedActivityInput = Pick<CreateClientProjectActivityInput, SharedActivityInputKey> &
  Pick<UpdateClientProjectActivityInput, SharedActivityInputKey>;

function toApiInputBase(data: WizardFormData): SharedActivityInput {
  const unitType = data.unitType ?? 'hours';

  let units: number;
  if (isHoursType(data.unitType)) {
    const hours = data.hoursInput ? parseInt(data.hoursInput, 10) : 0;
    const minutes = data.minutesInput ? parseInt(data.minutesInput, 10) : 0;
    units = hours + minutes / 60;
  } else if (data.unitType === 'lump_sum') {
    // Lump-sum disbursements always have units = 1; the entered amount is the rate
    units = 1;
  } else {
    units = data.units ? parseFloat(data.units) : 0;
  }

  return {
    clientProjectId: data.clientProjectId,
    type: (data.type as CreateClientProjectActivityInput['type']) ?? null,
    // Description is always required by the backend and by superRefine above.
    description: (data.description ?? '').trim(),
    date: new Date(data.date).toISOString(),
    units,
    // 0 means "no rate set" - send 0 when the field is empty
    ratePerUnitMinorUnits: parseRateInputToMinorUnits(data.ratePerUnitMinorUnits ?? '') ?? 0,
    currency: 'ZAR' as const,
    unitType,
    isDisbursement: data.isDisbursement ?? false,
  };
}

/**
 * On create, `null` means "no fee earner" and an omitted key means "let the
 * service default it" (the viewer's fee earner for a fee, none for a
 * disbursement). Only a visible picker may express a choice: with no picker
 * on screen the key is omitted, so a sentinel set by a type or checkbox
 * default never becomes an unassigned fee the user could not see.
 */
function toCreateInput(
  data: WizardFormData,
  idempotencyKey: string,
  performerPickerShown: boolean,
): CreateClientProjectActivityInput {
  const choice = data.performedByUserId === UNASSIGNED_PERFORMER ? null : data.performedByUserId || undefined;
  const performer = performerPickerShown && choice !== undefined ? { performedByUserId: choice } : {};

  const note = normaliseNote(data.note);

  return {
    ...toApiInputBase(data),
    idempotencyKey,
    ...performer,
    source: 'WEB_APP',
    ...(note !== null ? { note } : {}),
  };
}

function toUpdateInput(data: WizardFormData): UpdateClientProjectActivityInput {
  // The update schema's performedByUserId is .nullable().optional() - the
  // update service reads undefined as "leave unchanged" and null as "clear",
  // so the sentinel must map to null here to actually clear the performer.
  const performedByUserId =
    data.performedByUserId === UNASSIGNED_PERFORMER ? null : data.performedByUserId || undefined;

  return {
    ...toApiInputBase(data),
    performedByUserId,
  };
}


/** What an edit writes: the PATCH body and the PUT note, each only when it changed. */
interface ActivityUpdateRequest {
  /** PATCH body, or null when the entry's details are left alone. */
  details: UpdateClientProjectActivityInput | null;
  /** The new note: undefined leaves it alone, null clears it. */
  note: string | null | undefined;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActivityWizardFormProps {
  activity?: ClientProjectActivity;
  clientProjectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  /** Set status to APPROVED on save */
  approveOnSave?: boolean;
  /** Delete a DRAFT activity (called from invoice builder) */
  onDelete?: () => void;
  /**
   * Per-account billing increment in hours (e.g. 0.1 or 0.05) - input-time
   * steering only. When set and the entered duration is not a whole multiple
   * of it, a warning is shown below the hours/minutes inputs with a one-click
   * "Round up" action; the user's own input is always what gets saved unless
   * they take the action. When null, time is free-form and no warning is shown.
   */
  timeIncrement?: number | null;
  /**
   * Pre-filled time from the stopwatch timer (hours + minutes).
   * When provided alongside clientProjectId, the wizard starts at step 2
   * and pre-fills the time fields after type selection.
   */
  initialTime?: { hours: number; minutes: number };
  /**
   * Explicit client ID, used by the timer flow to avoid depending on
   * the matters array being loaded to derive the client.
   */
  initialClientId?: string;
  /**
   * Invoice id to invalidate after a successful update. Pass when the form is
   * rendered from an invoice editor: the backend listener re-creates the line
   * item snapshot asynchronously after the PATCH response, so we invalidate
   * the invoice query immediately and again after the server's sync window
   * to pick up the refreshed line item.
   */
  invoiceContextId?: string;
  /** Open with the file note revealed and focused, as when it is opened from the note marker. */
  focusNote?: boolean;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ActivityWizardForm({
  activity,
  clientProjectId,
  onSuccess,
  onCancel,
  approveOnSave = false,
  onDelete,
  timeIncrement = null,
  initialTime,
  initialClientId: initialClientIdProp,
  invoiceContextId,
  focusNote = false,
}: ActivityWizardFormProps) {
  const { accountId, user } = useAuth();
  const { account, role } = useAccount();
  const copy = useCopy();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Memoised so a null account (loading) doesn't hand the "Performed by"
  // effects below a fresh [] reference on every render.
  const members = useMemo(() => account?.members ?? [], [account?.members]);
  // The fee-earner picker: non-billing support roles never become candidate
  // performers, whether as a Select option or as the resolved default.
  // `members` (unfiltered) is kept for the performer lookups below.
  const feeEarnerMembers = useMemo(() => selectFeeEarnerMembers(members), [members]);
  const isMultiMember = feeEarnerMembers.length > 1;
  // A single-fee-earner account has no picker, except to show and fix a stored
  // fee that has no fee earner (for example one imported without a User Email).
  const performerPickerShown =
    isMultiMember || (activity != null && !activity.isDisbursement && activity.performedByUserId == null);

  // Step 1 (matter picker) is shown whenever no matter is pre-selected via prop.
  // Edit mode lands on step 3 so the user sees the details first; Back can walk
  // them to step 2 (type) and step 1 (matter) to re-pick if needed.
  // Pre-selected matter (including edits opened from a matter page) starts at step 2.
  const [step, setStep] = useState<1 | 2 | 3>(activity ? 3 : clientProjectId ? 2 : 1);
  const [matterSearchQuery, setMatterSearchQuery] = useState('');

  // Debounce matterSearchQuery for backend client search (300ms)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(matterSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [matterSearchQuery]);

  // A client named without a matter (the hub's "Log activity") offers only that client's matters.
  const pickerClientId = clientProjectId ? undefined : initialClientIdProp || undefined;

  const {
    data: matterPages,
    fetchNextPage: fetchMoreMatters,
    hasNextPage: hasMoreMatters,
    isFetchingNextPage: isFetchingMoreMatters,
    isError: _isMatterPagesError,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [...queryKeys.clientProjects(accountId!), 'picker', debouncedSearch, pickerClientId ?? null],
    queryFn: ({ pageParam }) =>
      listClientProjects(accountId!, {
        clientId: pickerClientId,
        cursor: pageParam,
        limit: DEFAULT_LIST_PAGE_SIZE,
        search: debouncedSearch || undefined,
        sortField: 'clientName',
        sortDirection: 'asc',
      }),
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
    enabled: !!accountId,
  });
  const matters = useMemo(() => flattenPages(matterPages?.pages ?? []), [matterPages?.pages]);
  const matterListRead = { isLoading };
  const matterListReveal = useRevealGroup([matterListRead]);

  const defaultDate = useMemo(
    () =>
      activity?.date
        ? toDateKey(activity.date)
        : toIsoDate(),
    [activity?.date],
  );

  // When editing a hours-based activity, split decimal hours into hours + minutes
  const editHoursInput = (() => {
    if (!activity || !activity.units || activity.type === undefined) return '';
    if (activity.unitType !== 'hours') return '';
    const totalMinutes = Math.round(activity.units * 60);
    return String(Math.floor(totalMinutes / 60));
  })();

  const editMinutesInput = (() => {
    if (!activity || !activity.units || activity.type === undefined) return '';
    if (activity.unitType !== 'hours') return '';
    const totalMinutes = Math.round(activity.units * 60);
    return String(totalMinutes % 60);
  })();

  // Fetch the specific matter when a clientProjectId is pre-provided (pre-selected or edit mode).
  // This avoids depending on the full matters list being loaded to derive the client.
  const knownMatterId = clientProjectId || activity?.clientProjectId;
  const { data: preselectedMatter, isError: _isPreselectedMatterError } = useQuery({
    queryKey: queryKeys.clientProject(accountId!, knownMatterId!),
    queryFn: () => getClientProject(accountId!, knownMatterId!),
    enabled: !!accountId && !!knownMatterId,
  });

  // A matter named only by the prop must be live: a stale link to a deleted matter
  // starts the entry with no matter. An existing activity keeps showing its own matter.
  const isPropMatterDeleted = !activity && !!clientProjectId && !!preselectedMatter?.archivedAt;
  const hasMatterStep = !clientProjectId || isPropMatterDeleted;

  const initialClientId = initialClientIdProp || preselectedMatter?.clientId || '';

  const wizardFormSchema = useMemo(() => createWizardFormSchema(copy), [copy]);

  // Read once at mount, like useForm's own defaultValues. Kept so the edit
  // path can tell whether Save changes the entry's details or only its note.
  const [initialFormValues] = useState<WizardFormData>(() => ({
    type: activity?.type ?? undefined,
    clientId: initialClientId,
    clientProjectId: clientProjectId || activity?.clientProjectId || '',
    performedByUserId: activity
      ? (activity.performedByUserId ?? UNASSIGNED_PERFORMER)
      : defaultFeeEarnerUserId(feeEarnerMembers, user?.id ?? '', role),
    description: activity?.description || '',
    note: activity?.note ?? '',
    date: defaultDate,
    unitType: activity?.unitType,
    isDisbursement: activity?.isDisbursement ?? false,
    // For hours-based types, units is managed via hoursInput + minutesInput.
    // 0 is the sentinel for "no value set" - do not pre-fill the field with "0".
    units:
      activity?.units !== undefined &&
      activity.units !== 0 &&
      activity.unitType !== 'hours'
        ? String(activity.units)
        : '',
    hoursInput: editHoursInput,
    minutesInput: editMinutesInput,
    ratePerUnitMinorUnits: activity?.ratePerUnitMinorUnits
      ? (activity.ratePerUnitMinorUnits / 100).toFixed(2)
      : '',
  }));

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    control,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<WizardFormData>({
    resolver: zodResolver(wizardFormSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: initialFormValues,
  });

  const clientIdRegistration = register('clientId');
  const clientProjectIdRegistration = register('clientProjectId');

  const selectedType = watch('type');
  const selectedClientId = watch('clientId');
  const selectedMatterId = watch('clientProjectId');
  const selectedPerformedByUserId = watch('performedByUserId');
  const selectedMember = members.find((m) => m.user.id === selectedPerformedByUserId);
  const currentHoursInput = watch('hoursInput');
  const currentMinutesInput = watch('minutesInput');
  const currentUnits = watch('units');
  const currentRate = watch('ratePerUnitMinorUnits');
  const currentUnitType = useWatch({ control, name: 'unitType' });
  const currentIsDisbursement = useWatch({ control, name: 'isDisbursement' });

  const getRate = useCallback(() => getValues('ratePerUnitMinorUnits'), [getValues]);
  const setRate = useCallback((value: string) => setValue('ratePerUnitMinorUnits', value), [setValue]);
  const defaultRate = useActivityDefaultRate({
    accountId,
    clientProjectId: selectedMatterId,
    performedByUserId:
      selectedPerformedByUserId && selectedPerformedByUserId !== UNASSIGNED_PERFORMER
        ? selectedPerformedByUserId
        : undefined,
    type: selectedType as ActivityType | null | undefined,
    unitType: currentUnitType,
    practiceSettings: account?.practiceSettings,
    performer: selectedMember,
    isEdit: !!activity,
    getRate,
    setRate,
  });

  // Source-email preview. An activity logged from an inbox email carries the
  // stored-email id on sourceRef - either EMAIL_AI_DETECTION (auto-detected) or
  // WEB_APP (manually allocated from the Emails page). The helper guards on
  // sourceRef so legacy rows with the source enum set but no ref do not fetch.
  const hasSourceEmail = !!activity && activityHasSourceEmail(activity);
  const sourceEmailId = hasSourceEmail ? activity.sourceRef : null;
  // Fetch failures on the source email are non-blocking: the rest of the form
  // is still editable, the preview section just doesn't render. The error
  // is captured so the ESLint query-safety rule passes; surface it as a quiet
  // inline note instead of a toast to keep the edit path unobstructed.
  const { data: sourceEmail, isError: isSourceEmailError } = useQuery({
    queryKey: queryKeys.storedEmail(accountId!, sourceEmailId ?? ''),
    queryFn: () => getStoredEmail(accountId!, sourceEmailId!),
    enabled: !!accountId && !!sourceEmailId,
    staleTime: Infinity,
  });
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(true);

  // Source-calendar preview. An activity created from a calendar match carries
  // the stored-calendar-event id on sourceRef. The helper guards on sourceRef
  // so legacy rows do not fetch. Same non-blocking-error treatment as email.
  const hasSourceCalendar = !!activity && activityHasSourceCalendar(activity);
  const sourceCalendarEventId = hasSourceCalendar ? activity.sourceRef : null;
  const { data: sourceCalendarEvent, isError: isSourceCalendarError } = useQuery({
    queryKey: queryKeys.storedCalendarEvent(accountId!, sourceCalendarEventId ?? ''),
    queryFn: () => getStoredCalendarEvent(accountId!, sourceCalendarEventId!),
    enabled: !!accountId && !!sourceCalendarEventId,
    staleTime: Infinity,
  });
  const [isCalendarPreviewOpen, setIsCalendarPreviewOpen] = useState(true);

  // When preselectedMatter loads async (edit mode or pre-selected matter), populate form fields.
  // defaultValues runs once on mount so the async query result must be applied via setValue.
  useEffect(() => {
    if (!preselectedMatter || isPropMatterDeleted) return;
    if (!watch('clientId')) {
      setValue('clientId', preselectedMatter.clientId, { shouldValidate: false });
    }
  // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [preselectedMatter?.clientId]);

  // The deleted-matter answer arrives after the first render, which already
  // seeded the matter and opened step 2, so take both back to the picker once.
  const [deletedMatterHandled, setDeletedMatterHandled] = useState(false);
  if (isPropMatterDeleted && !deletedMatterHandled) {
    setDeletedMatterHandled(true);
    setStep(1);
  }
  useEffect(() => {
    if (!isPropMatterDeleted) return;
    setValue('clientProjectId', '');
    setValue('clientId', '');
  }, [isPropMatterDeleted, setValue]);

  // Currently selected matter (for default-rate hint).
  // Prefer the matter from the server-side search results; fall back to the targeted preselected query.
  const currentMatter = selectedMatterId
    ? (matters.find((m) => m.id === selectedMatterId) ?? preselectedMatter)
    : undefined;

  // Client+matter groups for step 1 matter picker - active matters grouped by client.
  // Backend search handles filtering; clientName is embedded on each matter so no
  // separate client lookup is needed here.
  const clientMatterGroups = useMemo(() => {
    const grouped = new Map<string, { clientId: string; clientName: string; matters: ClientProject[] }>();

    for (const matter of matters) {
      if (matter.archivedAt) continue;

      const existing = grouped.get(matter.clientId);
      if (existing) {
        existing.matters.push(matter);
      } else {
        grouped.set(matter.clientId, { clientId: matter.clientId, clientName: matter.clientName, matters: [matter] });
      }
    }

    return [...grouped.values()].sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [matters]);

  // Selecting a matter on step 1 applies defaults and advances to the type selector.
  const handleMatterSelect = (clientId: string, matterId: string) => {
    setValue('clientId', clientId, { shouldValidate: true });
    setValue('clientProjectId', matterId, { shouldValidate: true });

    setStep(2);
  };

  // Clears the fields that only make sense for one "kind" of unit (a
  // duration vs a flat amount vs a counted quantity). Shared by
  // handleTypeSelect (switching between incompatible unit types) and the
  // Disbursement checkbox's onChange (toggling forces a unit-kind switch too,
  // hours <-> lump sum), so the two call sites don't drift on which fields
  // "incompatible unit types" actually means.
  const clearUnitValueFields = () => {
    setValue('ratePerUnitMinorUnits', '');
    setValue('units', '');
    setValue('hoursInput', '');
    setValue('minutesInput', '');
  };

  const handleTypeSelect = (type: ActivityType | null) => {
    // Re-selecting the current type is just "advance" - do not wipe the
    // rate/units/description the user already has (matters for edit mode when
    // the user navigates step 3 -> back -> step 2 -> same type).
    if (type === selectedType) {
      setStep(3);
      return;
    }

    // Capture unit types before and after to determine whether to clear rate/quantity fields.
    const prevUnitType = currentUnitType;
    const typeConfig = type !== null ? ACTIVITY_TYPE_REGISTRY[type] : null;
    const registryDefaultUnit: UnitType = typeConfig?.defaultUnitType ?? 'hours';
    // A FIXED_RATE account's per-email flat fee applies to Email billed as an
    // item, not the registry's hours default - default the wizard to Items so
    // the pre-filled flat rate is visible immediately.
    const emailFixedRateApplies =
      type === 'EMAIL' && emailFixedRateConfigured(account?.practiceSettings, selectedMember);
    const nextUnitType: UnitType = emailFixedRateApplies ? 'items' : registryDefaultUnit;

    setValue('type', type);
    setValue('unitType', nextUnitType);
    setValue('isDisbursement', typeConfig?.defaultIsDisbursement ?? false);

    // Native disbursement types (Travel, Printing, etc.) never render the
    // GENERAL-only Disbursement checkbox, so they take the same "no fee
    // earner" default here. The lump-sum unit-type default stays GENERAL-only:
    // native types already get their unit from the registry (Travel is km,
    // Printing is pages).
    //
    // On create the sentinel is set whether or not a picker is shown;
    // toCreateInput only sends it when one is. On edit it is set only when the
    // picker is shown, so a default the user cannot see never rewrites the
    // stored performer on save (toUpdateInput maps the sentinel to null).
    if (typeConfig?.defaultIsDisbursement && (!activity || performerPickerShown)) {
      setValue('performedByUserId', UNASSIGNED_PERFORMER);
    }

    // Clear rate and quantity fields when switching between incompatible unit types
    // (e.g. hours-based -> pages, hours-based -> km). Preserve values when staying within
    // the same unit category (e.g. MEETING -> DRAFTING, both hours).
    if (prevUnitType !== nextUnitType) {
      clearUnitValueFields();
      // The rate for the new unit arrives from the resolver; a saved entry
      // re-prices here too, because its old rate was for a different unit.
      defaultRate.followNextRate();
    }

    // Pre-fill time from stopwatch timer when selecting an hours-based type.
    // Applied on every type selection (not just the first) so the user doesn't
    // lose the timer value if they accidentally pick a non-time type and go back.
    if (initialTime && nextUnitType === 'hours') {
      setValue('hoursInput', String(initialTime.hours));
      setValue('minutesInput', String(initialTime.minutes));
    }

    // A FIXED_RATE email defaults to a single billable item - one email.
    if (emailFixedRateApplies) {
      setValue('units', '1'); // one email by default; user can change the quantity
    }

    // Description is always caller-supplied now - no auto-narration across any
    // type. Clear the field when the user picks a new type so they start fresh.
    setValue('description', '');

    setStep(3);
  };

  // Re-validate the units/rate pair fields whenever any of them change after a submit
  // attempt. react-hook-form only re-validates the field that changed, so cross-field
  // superRefine errors (e.g. error on hoursInput when minutesInput is updated) won't
  // clear on their own - we have to trigger them explicitly.
  useEffect(() => {
    if (!isSubmitted) return;
    void trigger(['hoursInput', 'minutesInput', 'units', 'ratePerUnitMinorUnits']);
  // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [currentHoursInput, currentMinutesInput, currentUnits, isSubmitted]);

  // A disbursement type or tick sets the "no fee earner" sentinel as a
  // default. Switching back to a fee must not leave it there unnoticed, so on
  // the true -> false edge of the derived isDisbursement (which covers both
  // the type picker and the checkbox, and does not fire on mount) a still-held
  // sentinel resets: a stored fee returns to its stored value, anything else
  // to the default fee earner (the assistant -> earliest-owner rule, not the
  // capturing user).
  const wasDisbursementRef = useRef(currentIsDisbursement);
  useEffect(() => {
    const wasDisbursement = wasDisbursementRef.current;
    wasDisbursementRef.current = currentIsDisbursement;
    if (!wasDisbursement || currentIsDisbursement) return;
    if (getValues('performedByUserId') !== UNASSIGNED_PERFORMER) return;
    setValue(
      'performedByUserId',
      activity && !activity.isDisbursement
        ? (activity.performedByUserId ?? UNASSIGNED_PERFORMER)
        : defaultFeeEarnerUserId(feeEarnerMembers, user?.id ?? '', role),
    );
  }, [currentIsDisbursement, activity, user, feeEarnerMembers, role, getValues, setValue]);

  // Apply the assistant-viewer default once account membership data resolves
  // after mount - defaultValues above runs only once, before useAccount()'s
  // query has necessarily settled, so a viewer whose role/owner data arrives
  // late would otherwise be stuck on the pre-resolution fallback (the
  // current user). Fires once, create mode only (edit mode always keeps the
  // activity's stored performer), and only while the field still holds that
  // fallback - i.e. the user has not yet touched the picker. There is
  // nothing for the user to have touched before members load anyway (the
  // Select's options come from `feeEarnerMembers`), so this is a safety net
  // more than an expected race.
  const autoPerformerAppliedRef = useRef(false);
  useEffect(() => {
    if (activity) return;
    if (autoPerformerAppliedRef.current) return;
    if (feeEarnerMembers.length === 0) return;
    // Spend the one shot right here, deliberately, whatever the guards below
    // decide - do not move this below the touched-check. defaultValues above
    // resolves this field through the same defaultFeeEarnerUserId call, so by
    // the time members are non-empty the field has usually already been
    // corrected before this effect ever runs. That means
    // `selectedPerformedByUserId !== user.id` on this first run typically
    // signals "defaultValues already did the work", not "the user picked
    // something". Latching the ref only when the effect goes on to apply a
    // value defers the shot onto a later render - the one where the user
    // genuinely does pick a value - and silently overwrites their choice.
    // Covered by "does not override a performer the user has already picked,
    // even if membership data changes again" in
    // tests/ActivityWizardForm.performedBy.test.tsx.
    // (Confirmed by trying it: moving this below the guards broke that exact
    // test, because the shot then lands on the user's genuine pick instead.)
    autoPerformerAppliedRef.current = true;
    if (selectedPerformedByUserId !== (user?.id ?? '')) return;
    const resolved = defaultFeeEarnerUserId(feeEarnerMembers, user?.id ?? '', role);
    if (resolved === selectedPerformedByUserId) return;
    setValue('performedByUserId', resolved);
  }, [activity, feeEarnerMembers, role, user, selectedPerformedByUserId, setValue]);

  const [createIdempotencyKey] = useState(() => crypto.randomUUID());

  // Pending files queued by ActivityAttachmentUploader in create mode.
  // Uploaded after the activity is created so they can be associated with an ID.
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState<File[]>([]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (input: CreateClientProjectActivityInput) => {
      const created = await createClientProjectActivity(accountId!, input);
      if (approveOnSave) {
        // Two-step: create as DRAFT, then immediately approve.
        // If the approve call fails, surface the error - the activity exists as DRAFT.
        await approveClientProjectActivities(accountId!, { activityIds: [created.id] });
      }

      // Upload any queued attachments - errors are non-fatal, surface via toast
      if (pendingAttachmentFiles.length > 0) {
        const uploadErrors = await uploadPendingAttachments(
          accountId!,
          created.id,
          pendingAttachmentFiles,
        );
        for (const e of uploadErrors) {
          // Errors are shown after mutation settles - stored here for use in onSuccess
          void e; // surfaced via toast in onSuccess below
        }
        // Store errors on the mutation result for use in onSuccess
        return { activity: created, uploadErrors };
      }

      return { activity: created, uploadErrors: [] };
    },
    onSuccess: ({ uploadErrors }) => {
      void invalidateActivityFinancialCaches(queryClient, accountId!);
      if (uploadErrors.length > 0) {
        // Surface attachment upload failures as warnings - activity was saved successfully
        const names = uploadErrors.map((e) => e.filename).join(', ');
        addToast({
          variant: 'warning',
          title: 'Activity saved, but some attachments failed',
          description: `Could not upload: ${names}`,
        });
      }
      onSuccess();
    },
    // Shown inline by SaveErrorAlert; having a handler suppresses the global toast.
    onError: () => undefined,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ details, note }: ActivityUpdateRequest) => {
      if (details) {
        const updated = await updateClientProjectActivity(accountId!, activity!.id, details);
        // When editing a DRAFT activity, approve it on save. Guard with the
        // updatedAt this edit just produced, so the approve refuses rather than
        // approving a version the user never saw if someone else changed the
        // entry in between. If the approve call fails, surface the error - the
        // edit itself already saved.
        if (activity?.status === 'DRAFT' && approveOnSave) {
          await approveClientProjectActivities(accountId!, {
            activityIds: [updated.id],
            expectedUpdatedAt: [{ id: updated.id, updatedAt: updated.updatedAt }],
          });
        }
      }
      // The PATCH above does not take the note; it has its own endpoint, open
      // at every status.
      if (note !== undefined) {
        await setClientProjectActivityNote(accountId!, activity!.id, note);
      }
    },
    onSuccess: () => {
      void invalidateActivityFinancialCaches(queryClient, accountId!);
      if (invoiceContextId) {
        // Server listener re-creates the line item asynchronously. Invalidate
        // now (picks up the activity change) and again after the dispatch
        // window (picks up the re-created line item snapshot).
        const invoiceKey = queryKeys.invoice(accountId!, invoiceContextId);
        queryClient.invalidateQueries({ queryKey: invoiceKey });
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: invoiceKey });
        }, 500);
      }
      onSuccess();
    },
    // The edit may have saved even though the approve after it failed, so the
    // cached row must not keep showing the pre-edit values.
    onError: () => {
      void invalidateActivityFinancialCaches(queryClient, accountId!);
    },
  });

  const mutation = activity ? updateMutation : createMutation;

  function onSubmit(data: WizardFormData) {
    if (!activity) {
      createMutation.mutate(toCreateInput(data, createIdempotencyKey, performerPickerShown));
      return;
    }

    const nextNote = normaliseNote(data.note);
    const noteChanged = nextNote !== normaliseNote(activity.note);
    const details = toUpdateInput(data);
    const detailsChanged = !sameActivityDetails(details, toUpdateInput(initialFormValues));
    // Saving a DRAFT as "Save & Approve" still has to run the update, which is
    // where the approve step lives.
    const approvesDraft = activity.status === 'DRAFT' && approveOnSave;

    if (!detailsChanged && !approvesDraft && !noteChanged) {
      onSuccess();
      return;
    }
    updateMutation.mutate({
      details: detailsChanged || approvesDraft ? details : null,
      note: noteChanged ? nextNote : undefined,
    });
  }

  // An invoiced entry's details are locked, so Save writes the note alone. It
  // skips the whole-form validation: the locked fields are not the user's to
  // fix, and an old entry that fails today's rules must not block its note.
  async function handleNoteOnlySave() {
    if (!activity || !(await trigger('note'))) return;
    const nextNote = normaliseNote(getValues('note'));
    if (nextNote === normaliseNote(activity.note)) return;
    updateMutation.mutate({ details: null, note: nextNote });
  }

  // View-only mode: an invoiced entry renders the same disabled form so it
  // stays readable rather than unreachable. The gate is shared with the
  // linked-activity card's Edit affordance - see activityEditBlockedReason. A
  // null reason means editable.
  const readOnlyReason: string | null = activity ? activityEditBlockedReason(activity) : null;
  const isReadOnly = readOnlyReason !== null;
  // The file note is internal and never billed, so an invoiced entry keeps it
  // editable while every other field stays locked. Any other reason for
  // read-only keeps the note read-only too.
  const noteEditable = activity ? activityNoteEditable(activity) : true;
  const isNoteOnlyMode = isReadOnly && noteEditable;
  const readOnlyNotice = isNoteOnlyMode ? copy('activity:note.invoicedNotice') : readOnlyReason;
  const currentNote = watch('note');
  const currentDescription = watch('description');
  const noteChanged = !!activity && normaliseNote(currentNote) !== normaliseNote(activity.note);

  // Linked-invoice notice: sibling of the read-only banner above, not a
  // replacement - an APPROVED activity already on a line item stays editable
  // (INVOICED is the only status the read-only gate covers for this reason),
  // but the user should know where the work already landed before they
  // change it. `useInvoicesForLineItems` resolves the single id via the
  // batched by-line-item endpoint rather than a bespoke single-invoice read.
  const linkedInvoiceLineItemIds = useMemo(
    () =>
      activity?.invoiceLineItemId && activity.status !== 'INVOICED'
        ? [activity.invoiceLineItemId]
        : [],
    [activity?.invoiceLineItemId, activity?.status],
  );
  const { invoiceByLineItemId: linkedInvoiceByLineItemId } = useInvoicesForLineItems(
    accountId,
    linkedInvoiceLineItemIds,
  );
  const linkedInvoice = activity?.invoiceLineItemId
    ? linkedInvoiceByLineItemId.get(activity.invoiceLineItemId) ?? null
    : null;

  // Compute display info from currentUnitType
  const lumpSum = currentUnitType === 'lump_sum';
  const hoursType = isHoursType(currentUnitType);

  // Allowed unit types for the currently selected activity type
  const allowedUnitTypes = selectedType
    ? ACTIVITY_TYPE_REGISTRY[selectedType as ActivityType]?.allowedUnitTypes ?? UNIT_TYPES
    : UNIT_TYPES;

  const features = getPracticeFeatures(account?.practiceSettings?.practiceType);
  // Type tiles offered on step 2: excludes deprecated types (moved to Expenses)
  // and, for a BUSINESS account, legal-only types (e.g. Court attendance).
  // GENERAL is rendered separately above, so filter it out here.
  const captureTypes = getCaptureActivityTypes(account?.practiceSettings?.practiceType).filter(
    (t) => t !== 'GENERAL',
  );

  // Lump sums are an amount, not a rate, so they carry no hint. The line is
  // reserved whenever the entry has a default rate so the fields below do not
  // move when the resolver answers.
  const showRateHint = !!selectedType && !lumpSum && !!currentUnitType && defaultRate.hasDefaultSource;
  const rateHint =
    showRateHint && currentUnitType
      ? describeRateHint(copy, {
          resolved: defaultRate.resolved,
          enteredRateMinorUnits: parseRateInputToMinorUnits(currentRate ?? ''),
          unitType: currentUnitType,
          performerName: selectedMember?.user.name,
        })
      : null;
  const hasNoDefaultRate = !activity && showRateHint && defaultRate.resolvedNoRate;

  // Off-increment steering below the hours/minutes inputs. The derivation is
  // shared with the inline linked-activity editor so a duration is steered
  // identically wherever it is typed. The round-up action only fills the
  // fields - it never submits or auto-converts; the user still reviews and saves.
  const incrementWarning = hoursType
    ? buildIncrementSteering(
        totalMinutesFromInputs(currentHoursInput ?? '', currentMinutesInput ?? ''),
        timeIncrement,
      )
    : null;

  const handleRoundUpToIncrement = () => {
    if (!incrementWarning) return;
    setValue('hoursInput', incrementWarning.roundedHoursInput, { shouldValidate: true, shouldTouch: true });
    setValue('minutesInput', incrementWarning.roundedMinutesInput, { shouldValidate: true, shouldTouch: true });
  };

  const dialogTitle = isReadOnly ? 'Activity Details' : activity ? 'Edit Activity' : 'Log New Activity';

  const showNext =
    (step === 1 && !!selectedMatterId) ||
    (step === 2 && selectedType !== undefined);
  const handleNext = () => setStep(step === 1 ? 2 : 3);

  const rateIsZero = !currentRate || parseFloat(currentRate) === 0;
  const unitsIsZero = hoursType
    ? (!currentHoursInput || parseInt(currentHoursInput, 10) === 0) && (!currentMinutesInput || parseInt(currentMinutesInput, 10) === 0)
    : !currentUnits || parseFloat(currentUnits) === 0;
  // Held back while the default rate is on its way, so it does not flash and
  // shift the fields below for an entry that is about to be priced.
  const showZeroValueWarning = !isReadOnly && !lumpSum && rateIsZero && unitsIsZero && !defaultRate.isResolving;

  return (
    // TODO: migrate this dialog to FormDialog's `fixedHeight` and `footer`
    // props - they replace the inline disableBodyScroll + flex h-[Npx] +
    // inner-scroll-region boilerplate below. See GenerateReportDialog for
    // the target shape.
    <FormDialog
      open={true}
      onClose={onCancel}
      title={dialogTitle}
      loading={mutation.isPending}
      maxWidth="max-w-2xl"
      disableBodyScroll
    >
      {/* Fixed-height flex column anchors the wizard at a stable size across
          all three steps. FormDialog's panel is content-sized (max-h only, no
          min-h), so without a concrete height here the dialog would grow and
          shrink to fit each step's natural content - causing the layout jump.
          h-[560px] is tuned to fit step 3's typical content comfortably.
          max-h-[70vh] clamps it on short viewports. The body scrolls
          internally when a step exceeds the frame. */}
      <div className="flex h-[560px] max-h-[70vh] flex-col gap-4">
        {/* Step indicator row: [Back] [dots + step name stacked] [Next]
            Both side slots are flex-1 so the centre stays pinned regardless
            of which buttons are visible. Invisible placeholders on each side
            reserve identical geometry when the real button is absent - no
            layout shift as the user steps through the wizard.

            Dropped entirely in view-only mode: there is no step to move to
            (both buttons are already withdrawn), so "Step 3 of 3" over a form
            nobody can navigate is chrome from a flow that isn't running. */}
        {!isReadOnly && (
        <div className="flex shrink-0 items-center gap-2">
          {/* Left slot - Back button */}
          <div className="flex flex-1 justify-start">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Go back"
              onClick={() => setStep(step === 3 ? 2 : 1)}
              className={step > 1 ? '' : 'invisible'}
              tabIndex={step > 1 ? undefined : -1}
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M10 3L5 8l5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </Button>
          </div>

          {/* Centre slot - progress dots, then step name, then "Step X of Y".
              Three rows always present so height is stable across all steps. */}
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1.5">
              {Array.from({ length: hasMatterStep ? 3 : 2 }, (_, i) => i + 1).map((s) => (
                <div
                  key={s}
                  className={`h-1.5 rounded-full transition-all ${
                    s <= (hasMatterStep ? step : step - 1) ? 'bg-primary' : 'bg-border'
                  } ${s === (hasMatterStep ? step : step - 1) ? 'w-6' : 'w-3'}`}
                />
              ))}
            </div>
            <span className="text-xs font-medium text-secondary">
              {step === 1 ? copy('activity:wizard.selectProjectStep') : step === 2 ? 'Select type' : 'Activity details'}
            </span>
            <span className="text-[10px] text-text-muted">
              Step {hasMatterStep ? step : step - 1} of {hasMatterStep ? 3 : 2}
            </span>
          </div>

          {/* Right slot - Next button.
              Step 1: shown when a matter is already selected (navigating back).
              Step 2: shown when a type is already selected (navigating back).
              Invisible placeholder preserves width on all other steps. */}
          <div className="flex flex-1 justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleNext}
              className={showNext ? '' : 'invisible'}
              tabIndex={showNext ? undefined : -1}
            >
              Next
              <svg width="14" height="14" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M6 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Button>
          </div>
        </div>
        )}

        {readOnlyNotice && (
          <div className="shrink-0">
            <Alert variant="info">{readOnlyNotice}</Alert>
          </div>
        )}

        {linkedInvoice && (
          <div className="shrink-0">
            <Alert variant="warning">
              {copy('activity:wizard.linkedInvoiceNotice')}{' '}
              <TextLink to={`/invoices/${linkedInvoice.id}`} tone="notice">
                {linkedInvoice.invoiceNumber ?? linkedInvoice.id}
              </TextLink>
              .
            </Alert>
          </div>
        )}

        {/* Scrollable step body - fills available height, scrolls internally.
            min-h-0 is required on a flex child for overflow-y-auto to work.
            pb-4 gives the last element breathing room from the scroll edge.
            pr-2 keeps form inputs from touching the scrollbar track.
            scrollbar-gutter: stable reserves the gutter so step 1/2 (short
            content) align horizontally with step 3 (scrolling content). */}
        <div
          className="min-h-0 flex-1 overflow-y-auto pb-4 pr-2"
          style={{ scrollbarGutter: 'stable' }}
        >
        {/* Step 1: Client + matter picker */}
        {step === 1 && (
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                width="15"
                height="15"
                fill="none"
                viewBox="0 0 16 16"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <circle cx="7" cy="7" r="4.5" />
                <path d="M10.5 10.5L14 14" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                aria-label={copy('activity:wizard.searchAriaLabel')}
                placeholder={copy('activity:wizard.searchPlaceholder')}
                value={matterSearchQuery}
                onChange={(e) => setMatterSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-border bg-bg-primary py-2 pl-9 pr-3 text-sm text-secondary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Client + matter list */}
            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
              <Reveal group={matterListReveal} read={matterListRead} placeholder={<MatterPickerSkeleton size="comfortable" />}>
              {clientMatterGroups.length === 0 ? (
                <p className="py-10 text-center text-sm text-text-muted">
                  {matterSearchQuery ? copy('activity:wizard.noSearchResults') : copy('activity:wizard.noActiveProjects')}
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {clientMatterGroups.map(({ clientId, clientName, matters: clientMatters }) => (
                    <li key={clientId}>
                      {/* Client header */}
                      <div className="bg-bg-subtle px-4 py-2">
                        <p className="font-serif text-sm font-semibold text-secondary">
                          {clientName}
                        </p>
                      </div>
                      {/* Matter rows */}
                      <ul>
                        {clientMatters.map((matter) => (
                          <li key={matter.id}>
                            <button
                              type="button"
                              onClick={() => {
                                if (selectedMatterId === matter.id) {
                                  setStep(2);
                                } else {
                                  handleMatterSelect(clientId, matter.id);
                                }
                              }}
                              className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary active:bg-primary/10 ${
                                selectedMatterId === matter.id
                                  ? 'bg-primary/5 text-primary'
                                  : 'hover:bg-primary/5'
                              }`}
                            >
                              <span className={`font-medium ${selectedMatterId === matter.id ? 'text-primary' : 'text-secondary'}`}>
                                {matter.name}
                                {getProjectDisplayReference(matter) && (
                                  <span className={`ml-1.5 text-xs font-normal ${selectedMatterId === matter.id ? 'text-primary/70' : 'text-text-muted'}`}>
                                    {getProjectDisplayReference(matter)}
                                  </span>
                                )}
                              </span>
                              <svg
                                className={`h-4 w-4 flex-shrink-0 ${selectedMatterId === matter.id ? 'text-primary' : 'text-text-muted'}`}
                                fill="none"
                                viewBox="0 0 16 16"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                aria-hidden="true"
                              >
                                <path d="M6 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
              </Reveal>
              {hasMoreMatters && (
                <button
                  type="button"
                  onClick={() => fetchMoreMatters()}
                  disabled={isFetchingMoreMatters}
                  className="w-full border-t border-border py-2.5 text-center text-sm text-text-muted transition-colors hover:bg-bg-subtle disabled:opacity-50"
                >
                  {isFetchingMoreMatters ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Type selector */}
        {step === 2 && (
          <div className="space-y-4">
            {/* General - shown at the top as the primary catch-all option.
                Rendered in the same grid layout as the Fees and Disbursements
                sections so all tile buttons share one size. */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
                General
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => {
                    if (selectedType === 'GENERAL') {
                      setStep(3);
                    } else {
                      handleTypeSelect('GENERAL');
                    }
                  }}
                  className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95 ${
                    selectedType === 'GENERAL'
                      ? 'border-primary bg-primary/5 font-medium text-primary'
                      : 'border-border bg-bg-primary text-secondary'
                  }`}
                >
                  General
                </button>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
                {copy('activity:wizard.feesTileHeading')}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {captureTypes.filter((t) => !ACTIVITY_TYPE_REGISTRY[t].defaultIsDisbursement).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      if (selectedType === type) {
                        setStep(3);
                      } else {
                        handleTypeSelect(type);
                      }
                    }}
                    className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95 ${
                      selectedType === type
                        ? 'border-primary bg-primary/5 font-medium text-primary'
                        : 'border-border bg-bg-primary text-secondary'
                    }`}
                  >
                    {getTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>

            {features.disbursements && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
                  {copy('activity:wizard.disbursementsTileHeading')}
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {captureTypes.filter((t) => ACTIVITY_TYPE_REGISTRY[t].defaultIsDisbursement).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        if (selectedType === type) {
                          setStep(3);
                        } else {
                          handleTypeSelect(type);
                        }
                      }}
                      className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95 ${
                        selectedType === type
                          ? 'border-primary bg-primary/5 font-medium text-primary'
                          : 'border-border bg-bg-primary text-secondary'
                      }`}
                    >
                      {getTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Expenses are money paid to a third party (advocate, sheriff,
                correspondent, Gazette), captured with the supplier's invoice
                and reclaimable VAT - things an activity cannot carry. The four
                deprecated disbursement types are no longer offered above; this
                tile routes to the Expenses feature instead. */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
                {copy('activity:wizard.expensesTileHeading')}
              </p>
              <button
                type="button"
                onClick={() => {
                  onCancel();
                  // Carry the selected matter so the expense is pre-attributed to it.
                  navigate(
                    selectedMatterId
                      ? `/expenses/new?clientProjectId=${encodeURIComponent(selectedMatterId)}`
                      : '/expenses/new',
                  );
                }}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-bg-primary px-3 py-2.5 text-left text-sm text-secondary transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
              >
                <span>
                  <span className="font-medium">Add an expense</span>
                  <span className="mt-0.5 block text-xs text-text-muted">
                    {copy('activity:wizard.expenseHint')}
                  </span>
                </span>
                <span aria-hidden="true" className="text-text-muted">&rarr;</span>
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Everything else on one scrollable page */}
        {step === 3 && (
          <form
            onSubmit={
              isNoteOnlyMode
                ? (e) => {
                    e.preventDefault();
                    void handleNoteOnlySave();
                  }
                : handleSubmit(onSubmit, (formErrors) => console.error('Activity form validation failed', formErrors))
            }
            className="space-y-4"
          >
            {hasSourceEmail && isSourceEmailError && (
              <p className="text-xs text-text-muted">
                Couldn&apos;t load the original email for this activity.
              </p>
            )}
            {hasSourceCalendar && isSourceCalendarError && (
              <p className="text-xs text-text-muted">
                Couldn&apos;t load the calendar event for this activity.
              </p>
            )}

            {/* Original-email preview. Only rendered when the activity has a
                resolvable stored email; the query gates on both source + sourceRef
                so legacy rows with partial data neither crash nor show a half
                state. Expanded by default so the source is visible without a
                click; the user can collapse it to reclaim vertical space. */}
            {hasSourceEmail && sourceEmail && (
              <div className="rounded-lg border border-border bg-bg-primary">
                <button
                  type="button"
                  onClick={() => setIsEmailPreviewOpen((open) => !open)}
                  aria-expanded={isEmailPreviewOpen}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-secondary hover:bg-bg-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Original email
                  </span>
                  <svg
                    className={`h-4 w-4 text-text-muted transition-transform ${isEmailPreviewOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isEmailPreviewOpen && (
                  <div className="border-t border-border p-3">
                    <EmailPreview email={sourceEmail} />
                  </div>
                )}
              </div>
            )}

            {/* Original-calendar-event preview. Mirrors the email preview shape
                so the source of a calendar-derived activity is visible at a glance
                and collapsible to reclaim vertical space. */}
            {hasSourceCalendar && sourceCalendarEvent && (
              <div className="rounded-lg border border-border bg-bg-primary">
                <button
                  type="button"
                  onClick={() => setIsCalendarPreviewOpen((open) => !open)}
                  aria-expanded={isCalendarPreviewOpen}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-secondary hover:bg-bg-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <span className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-primary" />
                    Original calendar event
                  </span>
                  <svg
                    className={`h-4 w-4 text-text-muted transition-transform ${isCalendarPreviewOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isCalendarPreviewOpen && (
                  <div className="border-t border-border p-3">
                    <CalendarEventPreview event={sourceCalendarEvent} />
                  </div>
                )}
              </div>
            )}

            {/* Matter + type summary pill. The header Back button covers the
                "Change matter" and "Change type" affordances, so no inline
                links here - this is a read-only reminder of the current
                selections. */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border bg-bg-subtle px-3 py-2 text-sm">
              {currentMatter ? (
                <>
                  <span className="font-medium text-secondary">{currentMatter.clientName}</span>
                  <span className="text-text-muted">·</span>
                  <span className="text-secondary">{currentMatter.name}</span>
                </>
              ) : null}
              {selectedType !== undefined && (
                <>
                  <span className="text-text-muted">·</span>
                  <span className="text-text-muted">{getTypeLabel(selectedType)}</span>
                </>
              )}
            </div>

            {/* 1. Billing fields - client/matter registered as hidden; selected in step 1 or pre-provided */}
            <input type="hidden" name={clientIdRegistration.name} ref={clientIdRegistration.ref} value={selectedClientId} readOnly />
            <input type="hidden" name={clientProjectIdRegistration.name} ref={clientProjectIdRegistration.ref} value={selectedMatterId} readOnly />

            {/* Fee earner picker - multi-member accounts, or a stored fee with no fee earner */}
            {performerPickerShown && (
              <Select
                id="performedByUserId"
                label="Performed by"
                error={errors.performedByUserId?.message}
                disabled={isReadOnly}
                {...register('performedByUserId')}
              >
                <option value="" disabled>Select team member</option>
                <option value={UNASSIGNED_PERFORMER}>{copy('activity:wizard.noFeeEarnerOption')}</option>
                {feeEarnerMembers.map((member) => (
                  <option key={member.user.id} value={member.user.id}>
                    {member.user.name}
                    {member.user.id === user?.id ? ' (you)' : ''}
                  </option>
                ))}
              </Select>
            )}

            <div className={isReadOnly ? 'pointer-events-none opacity-50' : undefined}>
              <Controller
                control={control}
                name="date"
                render={({ field }) => (
                  <CalendarPopoverButton
                    label="Date *"
                    value={field.value ?? null}
                    onChange={(iso) => {
                      setValue('date', iso, { shouldValidate: true, shouldTouch: true });
                    }}
                    allowClear={false}
                    errorState={Boolean(errors.date)}
                  />
                )}
              />
              {errors.date?.message && (
                <p className="mt-1 text-xs text-error">{errors.date.message}</p>
              )}
            </div>

            {/* Unit type is user-selectable for any activity type whose registry
                `allowedUnitTypes` offers more than one option (e.g. Email can be
                billed by Hours, Items, or Lump sum). Types with a single allowed
                unit hide the selector and rely on the registry default set by
                handleTypeSelect (or the stored activity on edit). isDisbursement
                stays GENERAL-only; every other type carries its registry default,
                populated by handleTypeSelect so submission is correct. */}
            {allowedUnitTypes.length > 1 && (
              <Controller
                control={control}
                name="unitType"
                render={({ field }) => (
                  <Select
                    id="unitType"
                    label="Unit type"
                    showRequiredMarker
                    error={errors.unitType?.message}
                    disabled={isReadOnly}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value as UnitType)}
                  >
                    <option value="" disabled>Select unit type</option>
                    {(allowedUnitTypes as readonly UnitType[]).map((unit) => (
                      <option key={unit} value={unit}>
                        {UNIT_TYPE_REGISTRY[unit].label}
                      </option>
                    ))}
                  </Select>
                )}
              />
            )}

            {selectedType === 'GENERAL' && (
              <Controller
                control={control}
                name="isDisbursement"
                render={({ field }) => (
                  <Checkbox
                    id="isDisbursement"
                    label={copy('activity:badge.disbursement')}
                    checked={field.value === true}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      field.onChange(checked);
                      // Leaving a disbursement resets a held sentinel in the
                      // effect above. The defaults below (no fee earner, lump
                      // sum) are per-action, so they live here rather than in
                      // an effect on currentIsDisbursement, which would also
                      // fire on mount and clobber a loaded disbursement.
                      //
                      // Toggling back to the value the activity was loaded
                      // with (edit mode) is an undo gesture, not a genuine
                      // unit-kind switch, so it restores the stored fields
                      // instead of wiping them - otherwise tick-then-untick
                      // zeroes a real stored amount.
                      if (activity && activity.isDisbursement === checked) {
                        setValue('unitType', activity.unitType);
                        setValue(
                          'ratePerUnitMinorUnits',
                          activity.ratePerUnitMinorUnits
                            ? (activity.ratePerUnitMinorUnits / 100).toFixed(2)
                            : '',
                        );
                        setValue(
                          'units',
                          activity.units !== undefined && activity.units !== 0 && activity.unitType !== 'hours'
                            ? String(activity.units)
                            : '',
                        );
                        setValue('hoursInput', editHoursInput);
                        setValue('minutesInput', editMinutesInput);
                        setValue(
                          'performedByUserId',
                          activity.performedByUserId ?? UNASSIGNED_PERFORMER,
                        );
                        return;
                      }

                      if (checked) {
                        // Same gate as handleTypeSelect.
                        if (!activity || performerPickerShown) {
                          setValue('performedByUserId', UNASSIGNED_PERFORMER);
                        }
                        setValue('unitType', 'lump_sum');
                      } else {
                        // This checkbox only renders under selectedType ===
                        // 'GENERAL', but looks the default up from
                        // selectedType (rather than hardcoding GENERAL) so it
                        // stays correct if that render guard ever changes.
                        // Falls back to 'hours' for a null/undefined type,
                        // matching handleTypeSelect's registryDefaultUnit.
                        const typeConfig = selectedType ? ACTIVITY_TYPE_REGISTRY[selectedType as ActivityType] : null;
                        setValue('unitType', typeConfig?.defaultUnitType ?? 'hours');
                      }
                      clearUnitValueFields();
                    }}
                    disabled={isReadOnly}
                  />
                )}
              />
            )}

            {!lumpSum && hoursType && (
              <div className="space-y-1">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    id="hoursInput"
                    type="text"
                    label="Hours"
                    placeholder="0"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    error={errors.hoursInput?.message}
                    disabled={isReadOnly}
                    onKeyDown={preventEnterSubmit}
                    {...register('hoursInput')}
                  />
                  <Input
                    id="minutesInput"
                    type="text"
                    label="Minutes"
                    placeholder="0"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    error={errors.minutesInput?.message}
                    disabled={isReadOnly}
                    onKeyDown={preventEnterSubmit}
                    {...register('minutesInput')}
                  />
                </div>
                {incrementWarning && (
                  <IncrementSteeringAlert
                    steering={incrementWarning}
                    onRoundUp={handleRoundUpToIncrement}
                  />
                )}
              </div>
            )}
            {selectedType !== null && selectedType !== undefined && !lumpSum && !hoursType && (
              <Input
                id="units"
                type="text"
                label={currentUnitType ? UNIT_TYPE_REGISTRY[currentUnitType].label : 'Units'}
                placeholder="1"
                error={errors.units?.message}
                disabled={isReadOnly}
                onKeyDown={preventEnterSubmit}
                {...register('units')}
              />
            )}

            <div>
              <Input
                id="ratePerUnitMinorUnits"
                type="text"
                label={
                  lumpSum
                    ? currentIsDisbursement === true
                      ? 'Disbursement amount (R)'
                      : 'Amount (R)'
                    : `Rate per ${currentUnitType ? UNIT_TYPE_REGISTRY[currentUnitType].labelSingular : 'unit'} (R)` /* field label uses full word, rate suffix uses labelShorthand */
                }
                placeholder={ratePlaceholder(currentUnitType)}
                error={errors.ratePerUnitMinorUnits?.message}
                disabled={isReadOnly}
                onKeyDown={preventEnterSubmit}
                {...register('ratePerUnitMinorUnits')}
              />
              {showRateHint && (
                <p className={`mt-1 min-h-4 text-xs ${hasNoDefaultRate ? 'text-warning' : 'text-secondary'}`}>
                  {hasNoDefaultRate ? copy('activity:wizard.noDefaultRate') : rateHint}
                </p>
              )}
              {defaultRate.resolveFailed && (
                <div role="alert" className="mt-2 text-xs text-error">
                  Rate unavailable. Enter a rate or{' '}
                  <TextButton onClick={defaultRate.retry}>retry</TextButton>.
                </div>
              )}
            </div>

            {showZeroValueWarning && (
              <p className="text-xs text-text-muted">
                This activity will appear on the invoice as a zero-value entry.
              </p>
            )}

            {/* 3. Description - always user-supplied; the line appears on the invoice. */}
            {/* TODO(required-marker parity): Date (CalendarPopoverButton, above)
                and Description (Textarea, below) still bake the "*" into their
                literal label text rather than an aria-hidden marker, unlike the
                Performed-by / Unit-type Selects on this same step. Fixing them
                needs a showRequiredMarker prop on CalendarPopoverButton and
                Textarea (Input and Checkbox too, for consistency), plus a sweep
                of their ~11 call sites app-wide - out of scope here; see
                apps/web/bill/CLAUDE.md "Apply UI improvements to neighbouring
                pages" for the follow-up. */}
            <div>
              <Textarea
                id="description"
                label="Description *"
                placeholder="Describe the work performed or expense incurred"
                rows={4}
                error={errors.description?.message}
                disabled={isReadOnly}
                aria-describedby="description-hint"
                {...register('description')}
              />
              <p id="description-hint" className="mt-1 text-xs text-text-muted">
                Shown on the invoice.
              </p>
            </div>

            {/* 3b. File note - internal, never on the invoice. Stays editable on an invoiced entry. */}
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <ActivityNoteField
                  id="note"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.note?.message}
                  editable={noteEditable}
                  autoFocus={focusNote}
                  updatedAt={activity?.note ? activity.noteUpdatedAt : null}
                  description={currentDescription ?? ''}
                />
              )}
            />

            {/* 4. Attachments. Still listed in view-only mode - the point of
                opening a locked entry is to read it, and the source documents
                are part of that. The uploader renders nothing when there are
                none to show. */}
            <ActivityAttachmentUploader
              accountId={accountId!}
              activityId={activity?.id}
              onPendingFilesChange={!activity ? setPendingAttachmentFiles : undefined}
              disabled={mutation.isPending}
              readOnly={isReadOnly}
            />

            {mutation.isError && (
              <SaveErrorAlert
                error={mutation.error}
                fallback={`Couldn't ${activity ? 'update' : 'create'} the activity. Try again.`}
              />
            )}

            {/* 5. Save / Delete - inline at the end of the scrolling body.
                Header Back + close (X) already cover navigate-back and cancel,
                so no redundant buttons here. */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {activity?.status === 'DRAFT' && onDelete && (
                <div className="sm:mr-auto">
                  <Button type="button" variant="danger" onClick={onDelete}>
                    Delete
                  </Button>
                </div>
              )}
              {(!isReadOnly || isNoteOnlyMode) && (
                <div className="sm:ml-auto">
                  <Button type="submit" loading={mutation.isPending} disabled={isNoteOnlyMode && !noteChanged}>
                    {isNoteOnlyMode
                      ? copy('activity:note.saveInvoiced')
                      : approveOnSave
                        ? 'Save & Approve'
                        : 'Save'}
                  </Button>
                </div>
              )}
            </div>
          </form>
        )}
        </div>
      </div>
    </FormDialog>
  );
}
